import compose from 'transmute/compose';
import { List, Map as ImmutableMap } from 'immutable';
import { buildTypedMap } from '../types/TypedMap';
import { updateCurrencyKeyInMap } from './objectUtils';
import { getFormattedName } from './userUtils';

// ----
// Aggregated Goals
// ----

const transformGoalsDisplayedAggregationToUseCurrency = (aggregation, currencyCode) => buildTypedMap({
  assigneeType: aggregation.assigneeType,
  amount: {
    currencyCode,
    value: aggregation.amount
  },
  isAllPipelines: aggregation.isAllPipelines
});
export const transformGoalsPayloadToUseCurrency = (payload, companyCurrency) => {
  const {
    currencyCode,
    displayedAggregationsByOwner,
    displayedAggregationsByTeam,
    ownerIdsWithoutGoals,
    teamIdsWithoutGoals,
    totalAggregatedGoals
  } = payload;
  return buildTypedMap({
    displayedAggregationsByOwner: ImmutableMap(updateCurrencyKeyInMap(displayedAggregationsByOwner, currencyCode || companyCurrency, transformGoalsDisplayedAggregationToUseCurrency)),
    displayedAggregationsByTeam: ImmutableMap(updateCurrencyKeyInMap(displayedAggregationsByTeam, currencyCode || companyCurrency, transformGoalsDisplayedAggregationToUseCurrency)),
    ownerIdsWithoutGoals: List(ownerIdsWithoutGoals),
    teamIdsWithoutGoals: List(teamIdsWithoutGoals),
    totalAggregatedGoals: buildTypedMap({
      assigneeType: totalAggregatedGoals.assigneeType,
      amount: {
        value: totalAggregatedGoals.amount,
        currencyCode: currencyCode || companyCurrency
      },
      isAllPipelines: totalAggregatedGoals.isAllPipelines
    })
  });
};

// ----
// Aggregated Goals Progress Breakdown
// ----

const transformGoalsProgressBreakdownToUseCurrency = (aggregation, currencyCode) => buildTypedMap({
  aggregatedClosedWonDealAmount: {
    currencyCode,
    value: aggregation.aggregatedClosedWonDealAmount
  },
  aggregatedGoalAmount: {
    currencyCode,
    value: aggregation.aggregatedGoalAmount
  }
});
export const transformGoalsBreakdownToUseCurrency = (payload, companyCurrency) => ImmutableMap(updateCurrencyKeyInMap(payload.goalProgressBreakdown, payload.currencyCode || companyCurrency, transformGoalsProgressBreakdownToUseCurrency));

// ----
// Aggregated Submissions
// ----

const transformSubmissionsDisplayedAggregationByOwnerToUseCurrency = (aggregation, currencyCode) => buildTypedMap(Object.assign({}, aggregation, {
  totalSubmissionAmount: aggregation.totalSubmissionAmount === null ? null : {
    currencyCode,
    value: aggregation.totalSubmissionAmount
  }
}));
const transformSubmissionsDisplayedAggregationByTeamToUseCurrency = (aggregation, currencyCode) => buildTypedMap(Object.assign({}, aggregation, {
  teamSubmissionAmount: aggregation.teamSubmissionAmount === null ? null : {
    currencyCode,
    value: aggregation.teamSubmissionAmount
  },
  total: aggregation.total === null ? null : {
    currencyCode,
    value: aggregation.total
  },
  userSubmissionAmount: aggregation.userSubmissionAmount === null ? null : {
    currencyCode,
    value: aggregation.userSubmissionAmount
  }
}));
export const transformSubmissionsPayloadToUseCurrency = (payload, companyCurrency) => {
  const {
    currencyCode,
    displayedAggregationsByOwner,
    displayedAggregationsByTeam,
    totalForecastSubmission
  } = payload;
  return buildTypedMap({
    displayedAggregationsByOwner: ImmutableMap(updateCurrencyKeyInMap(displayedAggregationsByOwner, currencyCode || companyCurrency, transformSubmissionsDisplayedAggregationByOwnerToUseCurrency)),
    displayedAggregationsByTeam: ImmutableMap(updateCurrencyKeyInMap(displayedAggregationsByTeam, currencyCode || companyCurrency, transformSubmissionsDisplayedAggregationByTeamToUseCurrency)),
    totalForecastSubmission: transformSubmissionsDisplayedAggregationByTeamToUseCurrency(totalForecastSubmission, currencyCode || companyCurrency)
  });
};

// ----
// Aggregated Deal Rollups
// ----

export const transformDealRollupsDisplayedAggregationCategoryToUseCurrency = (aggregation, currencyCode) => buildTypedMap(Object.assign({}, aggregation, {
  amount: {
    value: aggregation.amount,
    currencyCode
  },
  amountInHomeCurrency: {
    value: aggregation.amountInHomeCurrency,
    currencyCode
  },
  forecastedAmount: {
    value: aggregation.forecastedAmount,
    currencyCode
  }
}));
export const transformDealRollupsDisplayedAggregationToUseCurrency = (aggregation, currencyCode) => buildTypedMap(Object.assign({}, aggregation, {
  aggregationsByForecastCategory: List(aggregation.aggregationsByForecastCategory.map(category => transformDealRollupsDisplayedAggregationCategoryToUseCurrency(category, currencyCode))),
  totalAmount: {
    value: aggregation.totalAmount,
    currencyCode
  },
  totalAmountInHomeCurrency: {
    value: aggregation.totalAmountInHomeCurrency,
    currencyCode
  },
  totalForecastedAmount: {
    value: aggregation.totalForecastedAmount,
    currencyCode
  },
  activeUsersInTeam: aggregation.activeUsersInTeam,
  deactivatedUsersInTeam: aggregation.deactivatedUsersInTeam
}));
export const transformDealRollupsToUseCurrency = (payload, companyCurrency) => buildTypedMap({
  attainmentCategories: List(payload.attainmentCategories),
  currencyCode: payload.currencyCode,
  displayedAggregationsByOwner: ImmutableMap(updateCurrencyKeyInMap(payload.displayedAggregationsByOwner, payload.currencyCode || companyCurrency, transformDealRollupsDisplayedAggregationToUseCurrency)),
  detailedTeamIdsWithoutDeals: List(payload.detailedTeamIdsWithoutDeals),
  displayedAggregationsByTeam: ImmutableMap(updateCurrencyKeyInMap(payload.displayedAggregationsByTeam, payload.currencyCode || companyCurrency, transformDealRollupsDisplayedAggregationToUseCurrency)),
  excludedCategories: List(payload.excludedCategories),
  hasFilteredDeactivatedUsers: payload.hasFilteredDeactivatedUsers,
  ownerIdsWithoutDeals: List(payload.ownerIdsWithoutDeals),
  teamIdsWithoutDeals: List(payload.teamIdsWithoutDeals),
  totalRollUp: transformDealRollupsDisplayedAggregationToUseCurrency(payload.totalRollUp, payload.currencyCode || companyCurrency)
});

// ----
// Deals Search
// ----

export const transformDealsSearchPayloadToUseCurrency = (portalSettingBasedPayload, companyCurrency, totalAmountPayload) => Object.assign({}, portalSettingBasedPayload, {
  totalDealAmount: {
    value: (totalAmountPayload === null || totalAmountPayload === void 0 ? void 0 : totalAmountPayload.totalDealAmount) || 0,
    currencyCode: (totalAmountPayload === null || totalAmountPayload === void 0 ? void 0 : totalAmountPayload.currencyCode) || companyCurrency
  },
  totalOrWeightedDealAmount: {
    value: (portalSettingBasedPayload === null || portalSettingBasedPayload === void 0 ? void 0 : portalSettingBasedPayload.totalDealAmount) || 0,
    currencyCode: (portalSettingBasedPayload === null || portalSettingBasedPayload === void 0 ? void 0 : portalSettingBasedPayload.currencyCode) || companyCurrency
  }
});
export const buildUserInfo = ({
  ownerId,
  ownersMap,
  ownerIdToUserIdMap,
  usersToTeamNameMap
}) => row => {
  const userId = ownerIdToUserIdMap.get(ownerId);
  const owner = ownersMap.get(userId);
  const email = owner && owner.get('email');
  const teamName = usersToTeamNameMap.get(userId);
  return Object.assign({}, row, {
    id: ownerId,
    name: owner ? getFormattedName(owner) : '',
    email,
    isUser: true,
    teamName,
    isActive: owner && owner.get('isActive', false)
  });
};
export const buildTeamInfo = ({
  teamId,
  teamsMap,
  activeUsersInTeam,
  deactivatedUsersInTeam
}) => row => {
  // TODO: Fix issue where team name is sometimes undefined
  // when it reaches this function: https://git.hubteam.com/HubSpot/forecasting-team/issues/2312
  const name = teamsMap.getIn([teamId, 'name'], '');
  return Object.assign({}, row, {
    id: teamId,
    name,
    isUser: false,
    email: '',
    userCount: activeUsersInTeam + deactivatedUsersInTeam,
    deactivatedUserCount: deactivatedUsersInTeam
  });
};
export const buildTotalInfo = ({
  id,
  teamsMap
}) => row => {
  const teamName = teamsMap.getIn([id, 'name']);
  return Object.assign({}, row, {
    id,
    name: teamName,
    teamName,
    isTotalRow: true,
    userCount: 0
  });
};
export const buildDealGroupingRollups = ({
  dealRollup,
  closedDealGroupingIds
}) => row => {
  return dealRollup.get('aggregationsByForecastCategory').reduce((flattenedDealRollup, aggregation) => {
    const isClosedGrouping = closedDealGroupingIds.includes(aggregation.get('categoryOption'));
    if (!isClosedGrouping) {
      return Object.assign({}, flattenedDealRollup, {
        dealGroupings: Object.assign({}, flattenedDealRollup.dealGroupings, {
          [aggregation.get('categoryOption')]: aggregation.get('forecastedAmount')
        })
      });
    }
    return flattenedDealRollup.closed && flattenedDealRollup.closed.currencyCode ? Object.assign({}, flattenedDealRollup, {
      closed: {
        value: aggregation.get('forecastedAmount').value + flattenedDealRollup.closed.value,
        currencyCode: aggregation.get('forecastedAmount').currencyCode
      }
    }) : Object.assign({}, flattenedDealRollup, {
      closed: aggregation.get('forecastedAmount')
    });
  }, row);
};
export const buildCounts = ({
  dealRollup
}) => row => {
  const counts = dealRollup.get('aggregationsByForecastCategory').reduce((acc, aggregation) => Object.assign({}, acc, {
    [aggregation.get('categoryOption')]: aggregation.get('count')
  }), {});
  return Object.assign({}, row, {
    counts
  });
};
export const buildUserGoal = ({
  goal
}) => row => Object.assign({}, row, {
  goal
});
export const buildTeamGoal = ({
  goal,
  assigneeType
}) => row => Object.assign({}, row, {
  goal,
  assigneeType
});
export const buildTotalGoal = ({
  goal,
  assigneeType
}) => row => Object.assign({}, row, {
  goal,
  assigneeType
});
export const buildAttainment = () => row => {
  let attainment = 0;
  const goal = row.goal;
  const closed = row.closed;
  if (goal && goal.value && closed && closed.value) {
    attainment = closed.value / goal.value * 100;
  }
  return Object.assign({}, row, {
    attainment
  });
};
export const buildTotalForecastedAmount = ({
  dealRollup
}) => row => Object.assign({}, row, {
  totalForecastedAmount: dealRollup.get('totalForecastedAmount')
});
export const buildCoverage = () => row => {
  let coverage;
  const goal = row.goal;
  const totalForecastedAmount = row.totalForecastedAmount;
  if (goal && goal.value) {
    const amount = totalForecastedAmount && totalForecastedAmount.value || 0;
    coverage = amount / goal.value;
  }
  return Object.assign({}, row, {
    coverage
  });
};
export const buildOpenPipelineCoverage = ({
  closed,
  goal,
  openPipelineValue
}) => {
  if (openPipelineValue === 0) {
    return 0;
  }
  if (!goal || !openPipelineValue) {
    return null;
  }
  const gapToGoal = goal - closed > 0 ? goal - closed : 0;
  return gapToGoal ? openPipelineValue / gapToGoal : null;
};
export const buildUserSubmission = ({
  submission
}) => row => Object.assign({}, row, {
  forecastLastModified: submission.get('lastModifiedDate'),
  isAllPipelines: submission.get('isAllPipelines'),
  forecastValue: submission.get('totalSubmissionAmount', {
    currencyCode: '',
    value: 0
  })
});
export const buildTeamSubmission = ({
  submission
}) => row => {
  const lastModifiedDate = submission.get('lastModifiedDate', null);
  const teamSubmissionAmount = submission.get('teamSubmissionAmount');
  const forecastSubmission = lastModifiedDate && teamSubmissionAmount ? teamSubmissionAmount : submission.get('userSubmissionAmount');
  return Object.assign({}, row, {
    forecastLastModified: submission.get('lastModifiedDate', null),
    isAllPipelines: submission.get('isAllPipelines'),
    forecastValue: forecastSubmission || {
      currencyCode: '',
      value: 0
    },
    userSubmissionTotal: submission.get('userSubmissionAmount', {
      currencyCode: '',
      value: 0
    }),
    teamSubmissionAmount: teamSubmissionAmount || {
      currencyCode: '',
      value: 0
    }
  });
};
export const buildTotalSubmission = ({
  submission
}) => row => {
  return Object.assign({}, row, {
    forecastLastModified: submission.get('lastModifiedDate'),
    isAllPipelines: submission.get('isAllPipelines'),
    forecastValue: submission.get('total', {
      currencyCode: '',
      value: 0
    }),
    columnTotal: submission.get('total', {
      currencyCode: '',
      value: 0
    }),
    userSubmissionTotal: submission.get('userSubmissionAmount', {
      currencyCode: '',
      value: 0
    }),
    teamSubmissionAmount: submission.get('teamSubmissionAmount', {
      currencyCode: '',
      value: 0
    })
  });
};
export const buildDealRollup = ({
  dealRollup,
  closedDealGroupingIds
}) => row => dealRollup ? compose(buildCoverage(), buildTotalForecastedAmount({
  dealRollup
}), buildAttainment(), buildDealGroupingRollups({
  dealRollup,
  closedDealGroupingIds
}), buildCounts({
  dealRollup
}))(row) : row;
export const checkIfSubmissionDataIsLatest = ({
  isOwner,
  isCreate,
  id,
  fetchedSubmissionData,
  currentSubmissionData
}) => {
  let isLatest;
  const aggregationKey = isOwner ? 'displayedAggregationsByOwner' : 'displayedAggregationsByTeam';
  const currentSubmissionLastModifiedDate = currentSubmissionData.getIn([aggregationKey, id, 'lastModifiedDate']);
  const fetchedSubmissionLastModifiedDate = id && fetchedSubmissionData && fetchedSubmissionData[aggregationKey] && fetchedSubmissionData[aggregationKey][Number(id)] && fetchedSubmissionData[aggregationKey][Number(id)].lastModifiedDate;
  if (isCreate) {
    isLatest = !!fetchedSubmissionLastModifiedDate;
  } else {
    isLatest = fetchedSubmissionLastModifiedDate !== currentSubmissionLastModifiedDate;
  }
  return isLatest;
};